import produce from 'immer';
import * as _ from 'lodash';

import {
  Category,
  ChangeStatus,
  COUNT_MAX_FE,
  CREATED_AT_SORT,
  getGlobalScope,
  isProjectScope,
  isReleaseGroupScope,
  ISSUE_COUNT_SORT,
  isValidFoundAfterOption,
  SEVERITY_SORT,
} from '../../../shared/issuesV2';
import { errorMessagesListFromResponse } from '../../helpers/errors';
import { isValidCategory, isValidGrouping, isValidSort, isValidStatus, parseQueryParams } from '../../helpers/issuesV2';
import * as AsyncValue from '../asyncValue';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type {
  AffectedProject,
  AnySort,
  ConvertFoundAfterFilterForUser,
  FilterByCategory,
  GetLicenseIssuesLicensesListResponse,
  Grouping,
  IssueByCategory,
  IssueCWEsResponse,
  IssueExceptionsForDashboardResponse,
  IssuePackageManagersResponse,
  IssueRevision,
  IssueRevisionsResponse,
  IssuesCategorySummary,
  IssuesResponse,
  IssuesStatusSummary,
  IssueTypesSummary,
  LicensingFilter,
  Scope,
  Status,
  VulnFilter,
} from '../../../shared/issuesV2';
import type { ProjectType } from '../../../typings/enums';
import type { FailedFetchResponse } from '../../helpers/errors';
import type { ReduxAction, ReduxActionResponse, SyncReduxAction } from '../../types';

import { actionTypes } from './actions';
import {
  areAllIssuesSelected,
  clearSelection,
  deselectIssue,
  deselectRevision,
  isAnyIssueForRevisionSelected,
  isAnyIssueOnPageSelected,
  selectAll,
  selectIssue,
  selectRevision,
} from './selectionUtils';
import { byGrouping } from './utils';
import type { IssuesV2StoreState } from './selectionUtils';
import type { HydrateStateQuery, IssueRevisionWithIssues, RevisionLicensesResponse } from './types';

export const initialState: IssuesV2StoreState<Category> = Object.freeze({
  /**
   * AsyncStatus, tracks the network status as well as any error messages.
   */
  asyncStatus: {
    fetchIssues: getInitialStatus(),
    categorySummary: getInitialStatus(),
    statusSummary: getInitialStatus(),
    updateIssues: getInitialStatus(),
    issueDetails: getInitialStatus(),
    issueExceptions: getInitialStatus(),
    vulnDispute: getInitialStatus(),
    issueLicenses: getInitialStatus(),
    affectedProjects: getInitialStatus(),
    issueLicenseList: getInitialStatus(),
    extendIssueException: getInitialStatus(),
  },

  /**
   * Which "type" of issue we are looking at. Determined by user navigation & set on component mount.
   */
  issueCategory: Category.Licensing,

  /**
   * Revisions represents the VISIBLE Revisions, ie: the current "page" of Revisions with filters applied.
   */
  items: {
    grouping: 'revision' as const,
    revisions: AsyncValue.pending(),
    selection: {},
  },

  /**
   * This is the counts for each of the issue categories.
   * If this field is null, it is because they have not been fetched yet.
   * Category summary is only fetched once on initial mount.
   * @example
   * {
   *   licensing: 123,
   *   vulnerability: 4,
   *   quality: 66,
   * },
   */
  categorySummary: null,

  /**
   * This is the counts for each of the issue / revisions statuses.
   * If this field is null, it is because they have not been fetched yet.
   * Status summary is re-fetched when filters OR search change (but not when pagination or sorting change).
   * @example
   * {
   *   issues: {
   *     active: 123,
   *     ignored: 12,
   *   },
   *   revisions: {
   *     active: 123,
   *     ignored: 12,
   *   },
   * }
   */
  statusSummary: null,

  /**
   * This refers to the issue status indicators on the issue details page.
   * For global details, it will display the # of projects which are affected.
   * For the project details, it will include the Jira info for that project.
   */
  issueDetails: null,

  /**
   * Issue status refers to which "status tab" is active in the UI.
   * eg: 'active' | 'ignored'
   */
  status: 'active',

  /**
   * Scope refers to the "scope" of issues we are looking at,
   * either "global", "project" or "release group", along with data
   * such as the ID of the project/release group and the locator.
   */
  scope: getGlobalScope(),

  /**
   * Searchbar's search query
   */
  search: '',

  /**
   * This section holds info for each of the filters which a user might use
   * to narrow their results down.
   */
  filter: {
    /**
     * Depth refers to the "depth" of a given dependency.
     * ie: 'root', 'direct', or 'deep'
     */
    depths: undefined,

    /**
     * "type" here will vary based on the issue category, but will be tell us
     * more info about the issue, allowing the user to filter issues easier.
     * eg for licensing issues, this would be either "policy_conflict", "policy_flag", "unconcluded_dependency", or "unlicensed_dependency"
     */
    type: undefined,

    /**
     * Refers to container layer filters.
     * @example ['baseLayer', 'otherLayer']
     */
    containerLayers: undefined,
  },

  /**
   * Sorting is also dependent on category, but all categories are able to
   * sort by "found" ie: when the issue was created.
   */
  sort: ISSUE_COUNT_SORT.DESC,

  /**
   * Pagination information for when making requests.
   */
  page: 1,
  count: 20,

  /**
   * Project type if scope is project.
   * @example container
   */
  projectType: undefined,

  /**
   * Exceptions information
   */
  issueExceptions: {
    exceptions: [],
    total: 0,
  },
  /**
   * The id of the active issue for issue drawer
   */
  /**
   * The list of affected projects for the active issueDetails.
   */
  affectedProjects: null,
  /**
   * The list of licenses for the active issueDetails.
   */
  licenses: null,
  /**
   * The list of unique licenses present within the current issues.
   */
  issueLicenseList: [],
  /**
   * Count of all issues by IssueType.
   */
  typeSummary: AsyncValue.empty(),
  /**
   * The list of unique fetchers present within the current issues.
   */
  packageManagers: AsyncValue.empty(),
  /**
   * The list of unique CWEs present within the current issues.
   */
  cwes: AsyncValue.empty(),
  /**
   * Whether or not we're in comparison mode.
   */
  comparisonMode: false,
});

/**
 * Helper functions for resetting state, usually
 * after network requests happen. These help make the
 * intent clear in the reducers below.
 */

//  Pagination resets after every request.
const resetPage = <C extends Category>(draftState: IssuesV2StoreState<C>) => {
  draftState.page = initialState.page;
};

const resetSelectedIssues = <C extends Category>(draftState: IssuesV2StoreState<C>) => {
  draftState.items.selection = {};
};

/**
 * Clears the current issues, setting them back to a loading state.
 * This should be called whenever we want to begin loading a new page.
 */
const clearIssues = <C extends Category>(draftState: IssuesV2StoreState<C>) => {
  if (draftState.items.grouping === 'issue') {
    setIssues(draftState, []);
    draftState.asyncStatus.fetchIssues = getPendingStatus();
  } else if (draftState.items.grouping === 'revision') {
    setRevisionValue(draftState, AsyncValue.pending());
  }
};

/**
 * Clears and resets issues, resetting back to loading the first page with no selection.
 * This should be called whenever we want to completely reload issues, such as when filters change.
 */
const resetIssues = <C extends Category>(draftState: IssuesV2StoreState<C>) => {
  clearIssues(draftState);
  resetPage(draftState);
  resetSelectedIssues(draftState);
};

const resetIgnoreReasonFilter = <C extends Category>(draftState: IssuesV2StoreState<C>) => {
  // `ignoreReason` exists on vuln and licensing filters (not quality), so cast to the union.
  const filter = draftState.filter as VulnFilter | LicensingFilter;
  if (filter?.ignoreReason) {
    filter.ignoreReason = [];
  }
};

/**
 * Reset the current sorting based on category and grouping.
 */
const resetSort = <C extends Category>(draftState: IssuesV2StoreState<C>, category: C, grouping: Grouping) => {
  if (grouping === 'issue') {
    // by default, vulnerabilities should sort by severity descending to show highest critical vulns at the top
    draftState.sort = category === Category.Vulnerability ? SEVERITY_SORT.DESC : CREATED_AT_SORT.DESC;
  } else if (grouping === 'revision') {
    // by default, sort grouped revisions by issue count (descending)
    draftState.sort = ISSUE_COUNT_SORT.DESC;
  }
};

const findIssueById = <C extends Category>(
  state: IssuesV2StoreState<C>,
  issueId: number,
): IssueByCategory<C> | undefined =>
    byGrouping(
      state,
      (items) => items.issues.find((i) => i.id === issueId),
      (items) => items.revisions.value?.flatMap((r) => r.issues).find((i) => i.id === issueId),
    );

const setIssues = <C extends Category>(draftState: IssuesV2StoreState<C>, issues: Array<IssueByCategory<C>>) => {
  draftState.items = {
    grouping: 'issue',
    issues,
    selection: draftState.items.grouping === 'issue' ? draftState.items.selection : {},
  };
};

const setRevisionValue = <C extends Category>(
  draftState: IssuesV2StoreState<C>,
  revisions: AsyncValue.AsyncValue<Array<IssueRevisionWithIssues<Array<IssueByCategory<C>>>>>,
) => {
  draftState.items = {
    grouping: 'revision',
    revisions,
    selection: draftState.items.grouping === 'revision' ? draftState.items.selection : {},
  };
};

//  Reducer for the RESET state action.
const resetStateReducer = {
  //  Note: intentionally not using immer here, as we would need to override each
  //  field individually since it uses Proxies (you can't just do draftState = initialState).
  [actionTypes.RESET]: <C extends Category>(state: IssuesV2StoreState<C>): IssuesV2StoreState<C> => {
    const nextState = {
      ...initialState,
      //  Maintain scope across resets. This allows us to skip
      //  re-fetching the categorySummary if we are on the project issues page.
      scope: state.scope,
      // The chosen sort should stick around.
      sort: state.sort,
    } as IssuesV2StoreState<C>;

    // Preserve the grouping, but reset the items so they won't flash on the
    // page when the user returns.
    if (state.items.grouping === 'issue') {
      setIssues(nextState, [] as Array<IssueByCategory<C>>);
    } else if (state.items.grouping === 'revision') {
      setRevisionValue(nextState, AsyncValue.pending());
    }

    //  For projects + release groups, we always display the Issues dropdown nav item,
    //  which has all of the counts, so we want to keep this around.
    //  This is the same reason we maintain the scope above.
    if (isProjectScope(state.scope) || isReleaseGroupScope(state.scope)) {
      return {
        ...nextState,
        categorySummary: state.categorySummary,
      };
    }
    return nextState;
  },
};

//  Reducer for the SET_SCOPE action.
const setScopeReducer = {
  [actionTypes.SET_SCOPE]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ scope: Scope }>,
  ) =>
    produce(state, (draftState) => {
      //  We'll try to set scope when we click from a different page (ie: summary) to issues.
      //  In cases like this, the scope may not actually be different,
      //  and so if it isn't then we don't need to update anything and don't need to re-fetch categorySummary.
      if (!_.isEqual(state.scope, action.scope)) {
        draftState.categorySummary = null;
        draftState.scope = action.scope;
      }
    }),
  [actionTypes.SET_SCOPE_SCAN]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ scanId?: number }>,
  ) =>
    produce(state, (draftState) => {
      //  We need to reset the categorySummary so it will be re-fetched. See useCategorySummary for more info.
      draftState.categorySummary = null;

      if (isProjectScope(draftState.scope)) {
        draftState.scope.revisionScanId = action.scanId;
      }
      if (isReleaseGroupScope(draftState.scope)) {
        draftState.scope.releaseScanId = action.scanId;
      }
    }),
};

//  Reducer for the SET_PROJECT_TYPE action.
const setProjectTypeReducer = {
  [actionTypes.SET_PROJECT_TYPE]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ projectType?: ProjectType }>,
  ) =>
    produce(state, (draftState) => {
      draftState.projectType = action.projectType;
      if (action.projectType !== 'container' && draftState.filter) {
        draftState.filter.containerLayers = undefined;
      }
    }),
};

const setIssueCategoryReducer = {
  [actionTypes.SET_ISSUE_CATEGORY]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ issueCategory: Category }>,
  ) =>
    produce(state, (draftState) => {
      if (
        action.issueCategory &&
        isValidCategory(action.issueCategory) &&
        action.issueCategory !== draftState.issueCategory
      ) {
        resetIssues(draftState);
        draftState.issueCategory = action.issueCategory as C;
      }
    }),
};

const setStatusReducer = {
  [actionTypes.SET_STATUS]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ status: Status }>,
  ) =>
    produce(state, (draftState) => {
      if (action.status === 'active') {
        resetIgnoreReasonFilter(draftState);
      }
      resetIssues(draftState);
      draftState.status = action.status;
    }),
};

const setSortReducer = {
  [actionTypes.SET_SORT]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ sort: AnySort }>,
  ) =>
    produce(state, (draftState) => {
      if (isValidSort(draftState.issueCategory, draftState.items.grouping, action.sort)) {
        resetIssues(draftState);
        draftState.sort = action.sort;
      }
    }),
};

const setGroupingReducer = {
  [actionTypes.SET_GROUPING]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ grouping: Grouping }>,
  ) =>
    produce(state, (draftState) => {
      draftState.items.grouping = action.grouping;
      resetIssues(draftState);
      resetSort(draftState, draftState.issueCategory, action.grouping);
    }),
};

const selectIssuesReducer = {
  [actionTypes.SELECT_ISSUE]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ issueId: number }>,
  ) => {
    // Check the issue exists
    const issue = findIssueById(state, action.issueId);
    if (!issue) {
      return state;
    }

    return selectIssue(state, issue);
  },
  [actionTypes.DESELECT_ISSUE]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ issueId: number }>,
  ) => {
    // Check the issue exists
    const issue = findIssueById(state, action.issueId);
    if (!issue) {
      return state;
    }

    return deselectIssue(state, issue);
  },
  [actionTypes.TOGGLE_SELECT_ALL_ON_PAGE]: <C extends Category>(state: IssuesV2StoreState<C>) => {
    //  If all the issues are selected, then deselect everything.
    if (areAllIssuesSelected(state)) {
      return clearSelection(state);
    }
    //  If any of the issues on the current page are selected, deselect them (leaving any selection from other pages).
    if (isAnyIssueOnPageSelected(state)) {
      return byGrouping(
        state,
        ({ issues }) => issues.reduce(deselectIssue, state),
        ({ revisions: revisionsValue }) =>
          revisionsValue.value ? (revisionsValue.value as IssueRevision[]).reduce(deselectRevision, state) : state,
      );
    }
    // Finally, if none of the issues on the current page are selected, select them all.
    return byGrouping(
      state,
      ({ issues }) => issues.reduce(selectIssue, state),
      ({ revisions: revisionsValue }) =>
        revisionsValue.value ? revisionsValue.value.reduce(selectRevision, state) : state,
    );
  },
  [actionTypes.SELECT_ALL_ISSUES]: <C extends Category>(state: IssuesV2StoreState<C>) => selectAll(state),
  [actionTypes.CLEAR_SELECTION]: <C extends Category>(state: IssuesV2StoreState<C>) => clearSelection(state),
};

const selectRevisionReducer = {
  [actionTypes.SELECT_REVISION]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ revisionId: string }>,
  ) => {
    if (state.items.grouping === 'revision' && state.items.revisions.value) {
      const revision = state.items.revisions.value?.find(({ revisionId }) => revisionId === action.revisionId);
      if (!revision) {
        return state;
      }
      if (!isAnyIssueForRevisionSelected(state, revision)) {
        return selectRevision(state, revision);
      }
      return deselectRevision(state, revision);
    }
    return state;
  },
};

const setFilterReducer = {
  [actionTypes.SET_FILTER]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ filter: ConvertFoundAfterFilterForUser<C, FilterByCategory<C> | undefined> }>,
  ) =>
    produce(state, (draftState) => {
      if (action.filter) {
        resetIssues(draftState);
        draftState.filter = action.filter;
        draftState.asyncStatus.fetchIssues = getPendingStatus();
        draftState.asyncStatus.statusSummary = getPendingStatus();
      }
    }),
  // A reducer specifically for foundAfter that validates the value and so that
  // the caller doesn't have to mess with the entire filter object.
  [actionTypes.SET_FOUND_AFTER_FILTER]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ foundAfter: string | undefined }>,
  ) =>
    produce(state, (draftState) => {
      if (isValidFoundAfterOption(action.foundAfter)) {
        resetIssues(draftState);
        draftState.filter ??= {} as ConvertFoundAfterFilterForUser<C, FilterByCategory<C>>;
        draftState.filter.foundAfter = action.foundAfter;
      }
    }),
  [actionTypes.SET_PACKAGE_MANAGERS_FILTER]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ packageManagers: string[], skipResetIssues?: boolean }>,
  ) =>
    produce(state, (draftState) => {
      if (!action.skipResetIssues) {
        resetIssues(draftState);
      }
      draftState.filter ??= {} as ConvertFoundAfterFilterForUser<C, FilterByCategory<C>>;
      draftState.filter.packageManagers = action.packageManagers;
    }),
  [actionTypes.SET_CWES_FILTER]: <C extends Category.Vulnerability>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ cwes: string[] }>,
  ) =>
    produce(state, (draftState) => {
      resetIssues(draftState);
      draftState.filter ??= {} as ConvertFoundAfterFilterForUser<C, FilterByCategory<C>>;
      draftState.filter.cwes = action.cwes;
    }),
  [actionTypes.SET_PROJECT_LABELS_FILTER]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ projectLabels: string[] }>,
  ) =>
    produce(state, (draftState) => {
      resetIssues(draftState);
      draftState.filter ??= {} as ConvertFoundAfterFilterForUser<C, FilterByCategory<C>>;
      draftState.filter.projectLabels = action.projectLabels;
    }),
  [actionTypes.RESET_FILTERS]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      resetIssues(draftState);
      draftState.search = initialState.search;
      draftState.filter = {} as ConvertFoundAfterFilterForUser<C, FilterByCategory<C>>;
    }),
};

const preFetchLoadingReducer = {
  [actionTypes.PRE_FETCH_LOADING]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.fetchIssues = getPendingStatus();
    }),
};

const updateSearchReducer = {
  [actionTypes.SET_SEARCH]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ search: string }>,
  ) =>
    produce(state, (draftState) => {
      resetIssues(draftState);
      draftState.search = action.search || '';
    }),
};

const paginationReducer = {
  [actionTypes.SET_PAGE]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ page: number }>,
  ) =>
    produce(state, (draftState) => {
      draftState.page = action.page;
      clearIssues(draftState);
    }),
  [actionTypes.SET_COUNT]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ count: number }>,
  ) =>
    produce(state, (draftState) => {
      draftState.count = action.count;
      //  Reset the page to 1 when the count changes, or else they will be in a weird state.
      //  We also blow away their selected issues for the same reason.
      resetIssues(draftState);
    }),
};

const hydrateStateReducer = {
  [actionTypes.HYDRATE_STATE]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ query: HydrateStateQuery }>,
  ) =>
    produce(state, (draftState) => {
      //  Ensure that any query params are parsed into primatives (see parseQueryParams for more info).
      const query = parseQueryParams(action.query);

      // This action is only called with useFetchIssuesAction which will
      // dispatch a fetch.  The issues should be reset here to make sure that
      // old issues aren't available before the fetch actually starts.
      resetIssues(draftState);

      if (
        query.issueCategory &&
        isValidCategory(query.issueCategory) &&
        query.issueCategory !== draftState.issueCategory
      ) {
        draftState.issueCategory = action.query.issueCategory as C;
      }

      if (query.grouping && isValidGrouping(query.grouping) && query.grouping !== draftState.items.grouping) {
        if (query.grouping === 'revision') {
          setRevisionValue(draftState, AsyncValue.pending());
        } else {
          setIssues(draftState, []);
        }
      }

      // check if the incoming sort is valid and reset if it isn't
      const sort = query.sort ?? draftState.sort;
      if (isValidSort(draftState.issueCategory, draftState.items.grouping, sort)) {
        draftState.sort = sort;
      } else {
        resetSort(
          draftState,
          query.issueCategory ?? draftState.issueCategory,
          query.grouping ?? draftState.items.grouping,
        );
      }

      if (query.status && isValidStatus(query.status) && query.status !== draftState.status) {
        draftState.status = query.status;
      } else {
        draftState.status = initialState.status;
      }

      if (query.page && Number.isFinite(query.page) && draftState.page !== query.page && query.page >= 1) {
        draftState.page = query.page;
      } else {
        resetPage(draftState);
      }

      if (query.count) {
        const numericCount = query.count;
        //  If it's NaN/some garbage, we just ignore and don't set the count.
        if (Number.isFinite(numericCount)) {
          draftState.count = numericCount > COUNT_MAX_FE ? COUNT_MAX_FE : numericCount;
        }
      }

      draftState.search = query.search || '';
      draftState.filter = (query.filter ? query.filter : {}) as ConvertFoundAfterFilterForUser<C, FilterByCategory<C>>;
    }),
};

//  Reducers for FETCH_CATEGORY_SUMMARY actions
const fetchCategorySummaryReducers = {
  [`${actionTypes.FETCH_CATEGORY_SUMMARY}_PENDING`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesCategorySummary, { timestamp: number }>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.categorySummary = getPendingStatus();
      draftState.asyncStatus.categorySummary.timestamp = action.meta?.timestamp;
    }),
  [`${actionTypes.FETCH_CATEGORY_SUMMARY}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesCategorySummary, { timestamp: number }>,
  ) =>
    produce(state, (draftState) => {
      if (draftState.asyncStatus.categorySummary.timestamp === action.meta?.timestamp) {
        draftState.asyncStatus.categorySummary = getInitialStatus();
        draftState.categorySummary = action.payload.data ?? null;
      }
    }),
  [`${actionTypes.FETCH_CATEGORY_SUMMARY}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxAction,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.categorySummary = getErrorStatus(action.payload);
    }),
};

//  Reducers for the FETCH_STATUS_SUMMARY action.
const fetchStatusSummaryReducers = {
  [`${actionTypes.FETCH_STATUS_SUMMARY}_PENDING`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesStatusSummary, { timestamp: number }>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.statusSummary = getPendingStatus();
      draftState.asyncStatus.statusSummary.timestamp = action.meta?.timestamp;
    }),
  [`${actionTypes.FETCH_STATUS_SUMMARY}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesStatusSummary, { timestamp: number }>,
  ) =>
    produce(state, (draftState) => {
      if (draftState.asyncStatus.statusSummary.timestamp === action.meta?.timestamp) {
        draftState.asyncStatus.statusSummary = getInitialStatus();
        draftState.statusSummary = action.payload.data ?? null;
      }
    }),
  [`${actionTypes.FETCH_STATUS_SUMMARY}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesStatusSummary>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.statusSummary = getErrorStatus(action.payload);
    }),
};

//  Reducers for FETCH_ISSUESV2 actions
const fetchIssuesReducers = {
  [`${actionTypes.FETCH_ISSUES}_PENDING`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesResponse<C>, { timestamp: number }>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.fetchIssues = getPendingStatus();
      draftState.asyncStatus.fetchIssues.timestamp = action.meta?.timestamp;
      setIssues(draftState, []);
    }),
  [`${actionTypes.FETCH_ISSUES}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesResponse<C>, { timestamp: number }>,
  ) =>
    produce(state, (draftState) => {
      if (draftState.asyncStatus.fetchIssues.timestamp === action.meta?.timestamp) {
        draftState.asyncStatus.fetchIssues = getInitialStatus();
        setIssues(draftState, (action.payload.data?.issues ?? []) as Array<IssueByCategory<C>>);
      }
    }),
  [`${actionTypes.FETCH_ISSUES}_REJECTED`]: <C extends Category>(state: IssuesV2StoreState<C>, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.fetchIssues = getErrorStatus(action.payload);
    }),
};

const fetchIssueLicenseListReducers = {
  [`${actionTypes.FETCH_LICENSES_LIST}_PENDING`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueLicenseList = getPendingStatus();
      draftState.issueLicenseList = [];
    }),
  [`${actionTypes.FETCH_LICENSES_LIST}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<GetLicenseIssuesLicensesListResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueLicenseList = getInitialStatus();
      draftState.issueLicenseList = action.payload.data?.data ?? [];
    }),
  [`${actionTypes.FETCH_LICENSES_LIST}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<GetLicenseIssuesLicensesListResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueLicenseList = getErrorStatus(action.payload);
    }),
};

// Reducers for ISSUESV2_FETCH_ISSUE_LICENSES
const fetchIssueLicensesReducers = {
  [`${actionTypes.FETCH_ISSUE_LICENSES}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueLicenses = getPendingStatus();
      draftState.licenses = null;
    }),
  [`${actionTypes.FETCH_ISSUE_LICENSES}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<RevisionLicensesResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueLicenses.isLoading = false;
      if (action.payload.data) {
        draftState.licenses = action.payload.data;
      }
    }),
  [`${actionTypes.FETCH_ISSUE_LICENSES}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<RevisionLicensesResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueLicenses = getErrorStatus(action.payload);
    }),
};

//  Reducers for FETCH_REVISIONS actions
const fetchRevisionsReducers = {
  [`${actionTypes.FETCH_REVISIONS}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      setRevisionValue(draftState, AsyncValue.pending());
    }),
  [`${actionTypes.FETCH_REVISIONS}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueRevisionsResponse>,
  ) =>
    produce(state, (draftState) => {
      setRevisionValue(
        draftState,
        AsyncValue.resolve(
          // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: reducers must not change the action payload
          (action.payload.data?.revisions ?? []).map((revision) => ({
            ...revision,
            // Add an empty issue list and set the next page to the first one.
            issues: [],
            nextPage: AsyncValue.resolve(1),
            isExpanded: false,
          })),
        ),
      );
    }),
  [`${actionTypes.FETCH_REVISIONS}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxAction,
  ) =>
    produce(state, (draftState) => {
      setRevisionValue(draftState, AsyncValue.reject(errorMessagesListFromResponse(action.payload)));
    }),
};

//  Reducers for FETCH_REVISION_ISSUES actions
const fetchRevisionIssuesReducers = {
  [`${actionTypes.FETCH_REVISION_ISSUES}_PENDING`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxAction,
  ) =>
    produce(state, (draftState) => {
      if (draftState.items.grouping !== 'revision') {
        return;
      }
      const revisionIds = action.meta?.revisionIds as string[];
      if (!revisionIds) {
        return;
      }

      const revisions = draftState.items.revisions;

      revisions.value?.forEach((revision) => {
        if (revisionIds.includes(revision.revisionId)) {
          // eslint-disable-next-line no-param-reassign
          revision.nextPage = AsyncValue.setPending(revision.nextPage);
        }
      });
    }),
  [`${actionTypes.FETCH_REVISION_ISSUES}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesResponse<C>>,
  ) =>
    produce(state, (draftState) => {
      if (draftState.items.grouping !== 'revision' || !draftState.items.revisions.value) {
        return;
      }

      const revisions = draftState.items.revisions.value;

      // Typecast here because _.groupBy's type seems to be picking up a bunch of the prototype properties.
      const issuesByRevision = _.groupBy(
        action.payload.data?.issues ?? [],
        (i: IssueByCategory<C>) => i.source.id,
      ) as Record<string, Array<IssueByCategory<C>>>;

      Object.entries(issuesByRevision).forEach(([revisionId, issues]) => {
        const revision = revisions.find((r) => r.revisionId === revisionId);
        if (revision) {
          revision.issues = [...revision.issues, ...issues];
          revision.nextPage = AsyncValue.resolve((revision.nextPage.value ?? 0) + 1);
        }
      });
    }),
  [`${actionTypes.FETCH_REVISION_ISSUES}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxAction,
  ) =>
    produce(state, (draftState) => {
      if (draftState.items.grouping !== 'revision' || draftState.items.revisions.value === null) {
        return;
      }
      const revisionIds = action.meta?.revisionIds as string[];
      // Convert to unknown because the issue type does not matter.
      const revisions: Array<IssueRevisionWithIssues<unknown>> = draftState.items.revisions.value;

      revisionIds.forEach((revisionId) => {
        const revision = revisions.find((r) => r.revisionId === revisionId);
        if (revision) {
          revision.nextPage = AsyncValue.reject(errorMessagesListFromResponse(action.payload));
        }
      });
    }),
};

const revisionAccordionReducers = {
  [actionTypes.EXPAND_ISSUE_REVISION]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ revisionId: string }>,
  ) =>
    produce(state, (draftState) => {
      if (draftState.items.grouping !== 'revision') {
        return;
      }

      const revision = (draftState.items.revisions.value || ([] as Array<IssueRevisionWithIssues<unknown[]>>)).find(
        (r) => r.revisionId === action.revisionId,
      );
      if (revision) {
        revision.isExpanded = true;
      }
    }),
  [actionTypes.COLLAPSE_ISSUE_REVISION]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ revisionId: string }>,
  ) =>
    produce(state, (draftState) => {
      if (draftState.items.grouping !== 'revision') {
        return;
      }

      const revision = (draftState.items.revisions.value || ([] as Array<IssueRevisionWithIssues<unknown[]>>)).find(
        (r) => r.revisionId === action.revisionId,
      );
      if (revision) {
        revision.isExpanded = false;
      }
    }),
  [actionTypes.EXPAND_ALL_ISSUE_REVISIONS]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      if (draftState.items.grouping !== 'revision') {
        return;
      }

      (draftState.items.revisions.value || ([] as Array<IssueRevisionWithIssues<unknown[]>>)).forEach(
        (draftRevision) => {
          draftRevision.isExpanded = true;
          // If the first page of issues hasn't been loaded, set it to the pending state.
          if (draftRevision.issues.length === 0) {
            draftRevision.nextPage = AsyncValue.setPending(draftRevision.nextPage);
          }
        },
      );
    }),
  [actionTypes.COLLAPSE_ALL_ISSUE_REVISIONS]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      if (draftState.items.grouping !== 'revision') {
        return;
      }

      (draftState.items.revisions.value || ([] as Array<IssueRevisionWithIssues<unknown[]>>)).forEach(
        (draftRevision) => {
          draftRevision.isExpanded = false;
        },
      );
    }),
};

//  Reducers for UPDATE_ISSUES actions
const updateIssuesReducers = {
  [`${actionTypes.UPDATE_ISSUES}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.updateIssues = getPendingStatus();
    }),
  [`${actionTypes.UPDATE_ISSUES}_FULFILLED`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.updateIssues = getInitialStatus();
    }),
  [`${actionTypes.UPDATE_ISSUES}_REJECTED`]: <C extends Category>(state: IssuesV2StoreState<C>, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.updateIssues = getErrorStatus(action.payload);
    }),
  [actionTypes.CLEAR_UPDATE_ERRORS]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.updateIssues.errors = [];
    }),
};

const fetchIssueDetailsReducers = {
  [`${actionTypes.FETCH_ISSUE_DETAILS}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueDetails = getPendingStatus();
    }),
  [`${actionTypes.FETCH_ISSUE_DETAILS}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueByCategory<C>>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueDetails = getInitialStatus();

      const issue = action.payload.data;
      if (issue) {
        draftState.issueDetails = issue;
      } else {
        draftState.issueDetails = null;
      }
    }),
  [`${actionTypes.FETCH_ISSUE_DETAILS}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuesResponse<C>>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.issueDetails = getErrorStatus(action.payload);
    }),
  [actionTypes.CLEAR_ISSUE_DETAILS]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.issueDetails = initialState.issueDetails as IssueByCategory<C> | null;
      draftState.asyncStatus.issueDetails = getInitialStatus();
      draftState.affectedProjects = initialState.affectedProjects;
      draftState.licenses = initialState.licenses;
    }),
};

const fetchIssueExceptionsReducers = {
  [`${actionTypes.FETCH_ISSUE_EXCEPTIONS}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.issueExceptions = getPendingStatus();
    });
  },

  [`${actionTypes.FETCH_ISSUE_EXCEPTIONS}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueExceptionsForDashboardResponse>,
  ) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.issueExceptions = getErrorStatus(action.payload);
    });
  },

  [`${actionTypes.FETCH_ISSUE_EXCEPTIONS}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueExceptionsForDashboardResponse>,
  ) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.issueExceptions = getInitialStatus();
      draftState.issueExceptions.exceptions = action.payload.data?.exceptions || [];
      draftState.issueExceptions.total = action.payload.data?.total || 0;
    });
  },
};

const removeIssueExceptionsReducers = {
  [`${actionTypes.REMOVE_ISSUE_EXCEPTIONS}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.issueExceptions = getPendingStatus();
    });
  },
  [`${actionTypes.REMOVE_ISSUE_EXCEPTIONS}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<string>,
  ) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.issueExceptions = {
        loading: false,
        isLoading: false,
        errors: action.payload.data ? [action.payload.data] : null,
      };
    });
  },
  [`${actionTypes.REMOVE_ISSUE_EXCEPTIONS}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
  ) => {
    return produce(state, (draftState) => {
      // NOTE: we do not know which exceptions were removed, so a re-fetch will be necessary after a bulk delete.
      draftState.asyncStatus.issueExceptions = getInitialStatus();
    });
  },
};

const vulnDisputeReducers = {
  [`${actionTypes.POST_VULN_DISPUTE}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.vulnDispute = getPendingStatus();
    });
  },
  [`${actionTypes.POST_VULN_DISPUTE}_FULFILLED`]: <C extends Category>(state: IssuesV2StoreState<C>) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.vulnDispute = getInitialStatus();
    });
  },
  [`${actionTypes.POST_VULN_DISPUTE}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<FailedFetchResponse>,
  ) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.vulnDispute = getErrorStatus(action.payload.data as FailedFetchResponse);
    });
  },
};

const globalIssueProjectReducers = {
  [`${actionTypes.FETCH_AFFECTED_PROJECTS}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<AffectedProject[]>,
  ) => {
    return produce(state, (draftState) => {
      draftState.affectedProjects = action?.payload.data || [];
    });
  },
  [`${actionTypes.FETCH_AFFECTED_PROJECTS}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.affectedProjects = getPendingStatus();
    });
  },
  [`${actionTypes.FETCH_AFFECTED_PROJECTS}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<AffectedProject[]>,
  ) => {
    return produce(state, (draftState) => {
      draftState.asyncStatus.affectedProjects = getErrorStatus(action.payload);
    });
  },
};

const issueTypeSummaryReducer = {
  [`${actionTypes.FETCH_ISSUE_TYPE_SUMMARY}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.typeSummary = AsyncValue.pending();
    }),
  [`${actionTypes.FETCH_ISSUE_TYPE_SUMMARY}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueTypesSummary>,
  ) =>
    produce(state, (draftState) => {
      if (action.payload?.data) {
        draftState.typeSummary = AsyncValue.resolve(action.payload?.data);
      } else {
        draftState.typeSummary = AsyncValue.reject(['Unable to fetch type summary']);
      }
    }),
  [`${actionTypes.FETCH_ISSUE_TYPE_SUMMARY}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueTypesSummary>,
  ) =>
    produce(state, (draftState) => {
      draftState.typeSummary = AsyncValue.reject(errorMessagesListFromResponse(action.payload));
    }),
};

const fetchPackageManagersReducer = {
  [`${actionTypes.FETCH_PACKAGE_MANAGERS}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.packageManagers = AsyncValue.pending();
    }),
  [`${actionTypes.FETCH_PACKAGE_MANAGERS}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuePackageManagersResponse>,
  ) =>
    produce(state, (draftState) => {
      if (action.payload?.data) {
        draftState.packageManagers = AsyncValue.resolve(action.payload?.data?.packageManagers);
      } else {
        draftState.packageManagers = AsyncValue.reject(['Unable to fetch package managers']);
      }
    }),
  [`${actionTypes.FETCH_PACKAGE_MANAGERS}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssuePackageManagersResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.packageManagers = AsyncValue.reject(errorMessagesListFromResponse(action.payload));
    }),
};

const fetchCwesReducer = {
  [`${actionTypes.FETCH_CWES}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.cwes = AsyncValue.pending();
    }),
  [`${actionTypes.FETCH_CWES}_FULFILLED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueCWEsResponse>,
  ) =>
    produce(state, (draftState) => {
      if (action.payload?.data) {
        draftState.cwes = AsyncValue.resolve(action.payload?.data.cwes);
      } else {
        draftState.cwes = AsyncValue.reject(['Unable to fetch CWEs']);
      }
    }),
  [`${actionTypes.FETCH_CWES}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<IssueCWEsResponse[]>,
  ) =>
    produce(state, (draftState) => {
      draftState.cwes = AsyncValue.reject(errorMessagesListFromResponse(action.payload));
    }),
};

const extendIssueExceptionReducers = {
  [`${actionTypes.EXTEND_ISSUE_EXCEPTION}_PENDING`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.extendIssueException = getPendingStatus();
    }),
  [`${actionTypes.EXTEND_ISSUE_EXCEPTION}_FULFILLED`]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.extendIssueException = getInitialStatus();
    }),
  [`${actionTypes.EXTEND_ISSUE_EXCEPTION}_REJECTED`]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: ReduxActionResponse<FailedFetchResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.extendIssueException = getErrorStatus(action.payload.data as FailedFetchResponse);
    }),
};

const comparisonModeReducers = {
  [actionTypes.ENTER_COMPARISON_MODE]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.comparisonMode = true;
    }),
  [actionTypes.EXIT_COMPARISON_MODE]: <C extends Category>(state: IssuesV2StoreState<C>) =>
    produce(state, (draftState) => {
      draftState.comparisonMode = false;

      // Delete the compareTo from the scope (project/rg)
      if (isProjectScope(draftState.scope)) {
        delete draftState.scope.compareTo;
      } else if (isReleaseGroupScope(draftState.scope)) {
        delete draftState.scope.compareTo;
      }
    }),
  [actionTypes.SET_COMPARISON_REVISION]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ comparisonRevision: string }>,
  ) =>
    produce(state, (draftState) => {
      if (isProjectScope(draftState.scope)) {
        draftState.scope.compareTo = {
          changeStatus: draftState.scope.compareTo?.changeStatus ?? ChangeStatus.New,
          revision: action.comparisonRevision,
        };
      }
    }),
  [actionTypes.SET_COMPARISON_RELEASE]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ comparisonRelease: number }>,
  ) =>
    produce(state, (draftState) => {
      if (isReleaseGroupScope(draftState.scope)) {
        draftState.scope.compareTo = {
          changeStatus: draftState.scope.compareTo?.changeStatus ?? ChangeStatus.New,
          release: action.comparisonRelease,
        };
      }
    }),
  [actionTypes.SET_COMPARISON_CHANGE_STATUS]: <C extends Category>(
    state: IssuesV2StoreState<C>,
    action: SyncReduxAction<{ comparisonChangeStatus: ChangeStatus }>,
  ) =>
    produce(state, (draftState) => {
      if ((isProjectScope(draftState.scope) || isReleaseGroupScope(draftState.scope)) && draftState.scope.compareTo) {
        draftState.scope.compareTo.changeStatus = action.comparisonChangeStatus;
      }
    }),
};

export default createReducer<IssuesV2StoreState<Category>>(initialState, {
  ...resetStateReducer,
  ...setScopeReducer,
  ...setIssueCategoryReducer,
  ...setStatusReducer,
  ...setSortReducer,
  ...setGroupingReducer,
  ...setProjectTypeReducer,
  ...selectIssuesReducer,
  ...selectRevisionReducer,
  ...preFetchLoadingReducer,
  ...setFilterReducer,
  ...updateSearchReducer,
  ...paginationReducer,
  ...hydrateStateReducer,
  ...fetchCategorySummaryReducers,
  ...fetchStatusSummaryReducers,
  ...fetchIssuesReducers,
  ...fetchIssueLicensesReducers,
  ...fetchRevisionsReducers,
  ...fetchRevisionIssuesReducers,
  ...revisionAccordionReducers,
  ...updateIssuesReducers,
  ...fetchIssueDetailsReducers,
  ...fetchIssueExceptionsReducers,
  ...removeIssueExceptionsReducers,
  ...vulnDisputeReducers,
  ...globalIssueProjectReducers,
  ...issueTypeSummaryReducer,
  ...fetchPackageManagersReducer,
  ...fetchCwesReducer,
  ...fetchIssueLicenseListReducers,
  ...extendIssueExceptionReducers,
  ...comparisonModeReducers,
});
